import { Box, Card, CardContent, CardMedia, Chip, IconButton, Rating, Stack, Typography } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useState } from 'react';
import { otaColors } from '../theme/palette';

export interface OtaPropertySummary {
  id: number | string;
  name: string;
  city: string;
  area: string;
  primaryImageUrl?: string;
  /** All property image URLs. When more than one, a carousel is shown on the card. */
  imageUrls?: string[];
  rating?: number | null;
  reviewCount?: number | null;
  startingPrice: number;
  currency?: string;
  amenities?: string[];
  badge?: string;
  starRating?: number | null;
}

interface OtaPropertyCardProps {
  property: OtaPropertySummary;
  onClick?: (id: OtaPropertySummary['id']) => void;
}

/**
 * Hotel listing card used on the public OTA listing page.
 * Hover-elevation, clear price hierarchy, and badges for special tags.
 * When the property has multiple images, a carousel with arrow buttons and
 * dot indicators is shown instead of a static cover photo.
 */
export function OtaPropertyCard({ property, onClick }: OtaPropertyCardProps) {
  const currency = property.currency ?? '₹';

  // Build the ordered image list: prefer imageUrls, fall back to primaryImageUrl only.
  const images: string[] =
    property.imageUrls && property.imageUrls.length > 0
      ? property.imageUrls
      : property.primaryImageUrl
        ? [property.primaryImageUrl]
        : [];

  const fallback = 'https://placehold.co/600x400?text=Hotel';
  const [activeIdx, setActiveIdx] = useState(0);

  const goTo = (idx: number, e: React.MouseEvent) => {
    e.stopPropagation(); // don't fire card's onClick
    setActiveIdx(idx);
  };
  const prev = (e: React.MouseEvent) =>
    goTo(activeIdx === 0 ? images.length - 1 : activeIdx - 1, e);
  const next = (e: React.MouseEvent) =>
    goTo(activeIdx === images.length - 1 ? 0 : activeIdx + 1, e);

  return (
    <Card
      onClick={() => onClick?.(property.id)}
      sx={{
        cursor: onClick ? 'pointer' : 'default',
        overflow: 'hidden',
        // Uniform card heights across a row: Card stretches to fill its
        // Grid item, then internally lays out as a column so we can push
        // the price/CTA block to the bottom with mt: 'auto'. Without this,
        // a card with fewer amenities or no review badge ends up visibly
        // shorter than its row-mates.
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 12px 28px rgba(15, 23, 42, 0.12)',
          borderColor: 'primary.light',
        },
      }}
    >
      {/* ── Image / Carousel ────────────────────────────────────── */}
      <Box sx={{ position: 'relative', height: 200, overflow: 'hidden' }}>
        <CardMedia
          component="img"
          height="200"
          image={images[activeIdx] ?? fallback}
          alt={`${property.name} — photo ${activeIdx + 1}`}
          sx={{ objectFit: 'cover' }}
        />

        {/* Arrows — only when there are multiple images */}
        {images.length > 1 && (
          <>
            <IconButton
              size="small"
              onClick={prev}
              sx={{
                position: 'absolute', left: 6, top: '50%', transform: 'translateY(-50%)',
                bgcolor: 'rgba(0,0,0,0.45)', color: '#fff', p: 0.5,
                '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' },
              }}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={next}
              sx={{
                position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                bgcolor: 'rgba(0,0,0,0.45)', color: '#fff', p: 0.5,
                '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' },
              }}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>

            {/* Dot indicators */}
            <Stack
              direction="row"
              spacing={0.5}
              sx={{
                position: 'absolute', bottom: 7, left: '50%', transform: 'translateX(-50%)',
                pointerEvents: 'none',
              }}
            >
              {images.map((_, i) => (
                <Box
                  key={i}
                  sx={{
                    width: i === activeIdx ? 14 : 6,
                    height: 6,
                    borderRadius: 3,
                    bgcolor: i === activeIdx ? '#fff' : 'rgba(255,255,255,0.55)',
                    transition: 'width 0.2s, background-color 0.2s',
                  }}
                />
              ))}
            </Stack>
          </>
        )}

        {/* Badge overlay */}
        {property.badge && (
          <Chip
            label={property.badge}
            color="secondary"
            size="small"
            sx={{
              position: 'absolute', top: 12, left: 12,
              fontWeight: 600,
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            }}
          />
        )}
      </Box>

      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Title row — property name + the property's STAR CLASSIFICATION
            (i.e. "this is a 4-star hotel"). Rendered as filled stars only,
            no number, because the number of stars IS the data. Keeping
            classification inline with the name makes it read like "Hotel
            Sahara Star ★★★★★" not as a separate rating data point. */}
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
          <Typography variant="h6" sx={{ color: 'text.primary', flex: 1, minWidth: 0 }} noWrap>
            {property.name}
          </Typography>
          {property.starRating != null && property.starRating > 0 && (
            <Rating value={property.starRating} max={5} precision={0.5} readOnly size="small" />
          )}
        </Stack>

        {/* Location row */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          sx={{ mb: 1, color: 'text.secondary' }}
        >
          <LocationOnIcon fontSize="small" />
          <Typography variant="body2" noWrap>
            {property.area}, {property.city}
          </Typography>
        </Stack>

        {/* GUEST REVIEW row — only when there's at least one real review.
            Visually distinct from the star classification above: a colored
            pill badge with the score, the qualitative label ("Excellent",
            "Very Good", …), and the review count. No star icons here, so
            it can't be confused with classification. */}
        {property.rating != null && (property.reviewCount ?? 0) > 0 && (
          <ReviewBadge rating={property.rating} count={property.reviewCount ?? 0} />
        )}

        {property.amenities && property.amenities.length > 0 && (
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
            {property.amenities.slice(0, 3).map((a) => (
              <Chip key={a} label={a} size="small" variant="outlined" />
            ))}
          </Stack>
        )}

        {/* Price block — pushed to the bottom via mt: 'auto' so it stays
            aligned across cards with different amounts of content above it. */}
        <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mt: 'auto' }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Starts from
            </Typography>
            <Typography
              variant="h5"
              sx={{ color: otaColors.primary[600], fontWeight: 700, lineHeight: 1.2 }}
            >
              {currency}
              {property.startingPrice.toLocaleString('en-IN')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              per night
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

/**
 * Color-coded guest-review badge — visually distinct from the star
 * classification row so the two can never be confused. Mirrors the
 * RatingBadge pattern from PropertyListingCard for consistency between
 * the home page card and the listing page card.
 */
function ReviewBadge({ rating, count }: { rating: number; count: number }) {
  const color =
    rating >= 4.5 ? '#00A859'        // success / excellent
    : rating >= 4.0 ? '#2E7D32'      // very good
    : rating >= 3.5 ? '#1F8DD6'      // good
    : rating >= 2.5 ? '#F2A93B'      // fair
    :                 '#E74C3C';     // poor

  const label =
    rating >= 4.5 ? 'Excellent'
    : rating >= 4.0 ? 'Very Good'
    : rating >= 3.5 ? 'Good'
    : rating >= 2.5 ? 'Fair'
    :                 'Poor';

  return (
    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1.5 }}>
      <Box
        sx={{
          bgcolor: color,
          color: '#fff',
          px: 1,
          py: 0.25,
          borderRadius: 1,
          fontWeight: 700,
          fontSize: '0.85rem',
          minWidth: 38,
          textAlign: 'center',
        }}
      >
        {rating.toFixed(1)}
      </Box>
      <Stack spacing={0}>
        <Typography variant="caption" sx={{ color, fontWeight: 700, lineHeight: 1.1 }}>
          {label}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.1 }}>
          {count} review{count === 1 ? '' : 's'}
        </Typography>
      </Stack>
    </Stack>
  );
}
